import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMaterialReactTable,
} from 'material-react-table';
import { FC, useState } from 'react';
import ConfirmImportDevicesModal from '@/components/modals/confirm-import-devices';
import { HeadingGroup } from '@/components/ui/heading-group';
import { StatusBadge } from '@/components/ui/status-badge';
import { strings } from '@/constants/strings';
import { HeadingGroupContainerLayout } from '@/layouts/heading-group-container-layout';

import { useGetFloatingDevicesQuery } from '@/redux/api/endpoints/device-import';
import { composeTableOptions, getFilteredSelectedRows } from '@/table';
import { AlexaDevice } from '@/types/device';
import { DeviceType } from '@/types/device';
import { getDeviceTypeLabel } from '@/utils/device-helpers';
import { ImportHexnodeDeviceForm } from './import-hexnode-device-form';
import { SelectedAlexaDevicesActionBar } from './selected-alexadevices-action-bar';

const columns: MRT_ColumnDef<AlexaDevice>[] = [
  {
    accessorKey: 'serialNumber',
    header: 'Device Serial Number (DSN)',
    size: 150,
  },
  {
    accessorKey: 'model',
    header: 'Model',
    filterVariant: 'multi-select',
    size: 150,
  },
  {
    id: 'status',
    accessorFn: ({ connectivity }) => connectivity?.status,
    header: 'Status',
    grow: false,
    filterVariant: 'multi-select',
    size: 100,
    Cell: ({ row }) => {
      const connectivityStatus = row.original.connectivity?.status ?? 'Unknown';
      return (
        <StatusBadge
          text={connectivityStatus}
          variant={connectivityStatus === 'Online' ? 'success' : 'alert'}
        />
      );
    },
  },
];

type ImportTab = DeviceType;

const importTabs: { value: ImportTab; label: string }[] = [
  { value: 'ALEXA', label: getDeviceTypeLabel('ALEXA') },
  { value: 'HEX_NODE_TABLET', label: getDeviceTypeLabel('HEX_NODE_TABLET') },
];

/**
 * This page is used to import devices into the Platform.
 * Alexa devices are discovered from ASP, while Webhook and HexNode devices
 * are added manually via forms.
 */
export const ImportingPage: FC = () => {
  const [activeTab, setActiveTab] = useState<ImportTab>('ALEXA');
  const { data: devices = [], isLoading, isFetching } = useGetFloatingDevicesQuery(); // prettier-ignore

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const handleComplete = () => {
    setIsModalOpen(false);
    setRowSelection({});
  };

  const table = useMaterialReactTable({
    ...composeTableOptions<AlexaDevice>({
      enableRowActions: true,
      enableRowSelection: true,
    }),
    columns,
    enableColumnFilters: true,
    enableFacetedValues: true,
    data: devices,
    getRowId: (device) => device.endpointId,
    state: {
      isLoading,
      showColumnFilters: true,
      rowSelection,
      showProgressBars: isFetching,
      ...(isLoading
        ? {
            pagination: {
              pageIndex: 0,
              pageSize: 1,
            },
          }
        : {}),
    },
    onRowSelectionChange: setRowSelection,
  });

  const selectedDevices = getFilteredSelectedRows(table);

  return (
    <>
      <HeadingGroupContainerLayout>
        <HeadingGroup
          subtitlePrefix={strings.importing.subtitle}
          title={strings.importing.title}
        />
      </HeadingGroupContainerLayout>

      <div className="mb-6 flex gap-2">
        {importTabs.map((tab) => (
          <button
            className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.value
                ? 'bg-gray-800 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'ALEXA' && (
        <>
          <SelectedAlexaDevicesActionBar
            linkDeviceButtonText={`Import device${selectedDevices.length > 1 ? 's' : ''}`}
            onLinkDevicesClick={() => setIsModalOpen(true)}
            selectedDevices={selectedDevices}
          />
          <MaterialReactTable table={table} />

          <ConfirmImportDevicesModal
            isOpen={isModalOpen}
            onCancel={() => setIsModalOpen(false)}
            onComplete={handleComplete}
            selectedDevices={selectedDevices}
          />
        </>
      )}

      {activeTab === 'HEX_NODE_TABLET' && (
        <div className="mx-auto max-w-lg rounded-lg border border-gray-200 p-6">
          <h3 className="mb-4 text-lg font-semibold">Import HexNode Tablet</h3>
          <ImportHexnodeDeviceForm />
        </div>
      )}
    </>
  );
};
